import { UnitsEnum, useGetAssemblyPipeSpecLazyQuery } from '@/__generated__/graphql';
import { selectedAssemblyId, unitSystem as unitSystemState } from '@/components/Analysis/state';
import { localiseMeasurement } from '@/utils/unitSystem';
import { useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Detail, DetailsAccordion } from './StyledComponents';
import { Skeleton, Typography } from '@mui/material';

const SkeletonLoading = () =>
  Array.from({ length: 4 }, (_, index) => <Skeleton key={index} width="100%" height={32} />);

export const PipeSpec = () => {
  const assemblyId = useAtomValue(selectedAssemblyId);
  const [getPipeSpec, { data, loading, error }] = useGetAssemblyPipeSpecLazyQuery();

  useEffect(() => {
    if (assemblyId) getPipeSpec({ variables: { assemblyId } });
  }, [assemblyId, getPipeSpec]);

  const unitSystem = useAtomValue(unitSystemState);

  const { title, ...pipeSpecs } = useMemo(() => {
    const pipeSpec = data?.assembly?.pipeSpec;
    if (!pipeSpec) return { title: 'Pipe Spec' };

    const { spec, size, schedule, material, nominalThickness, nominalCorrosionAllowance } =
      pipeSpec;
    return {
      title: `Pipe Spec${spec ? ` - ${spec}` : ''}`,
      'Pipe size': `${size}, ${schedule || ''}`,
      Material: material,
      'Nominal thickness': localiseMeasurement({
        unitSystem,
        value: nominalThickness ?? 0,
        showUnitLabel: true,
        sourceUnit: UnitsEnum.Millimeter,
      }),
      'Nominal corrosion allowance': localiseMeasurement({
        unitSystem,
        value: nominalCorrosionAllowance ?? 0,
        showUnitLabel: true,
        sourceUnit: UnitsEnum.Millimeter,
      }),
    };
  }, [data?.assembly?.pipeSpec, unitSystem]);

  const pipeSpecsArray = Object.entries(pipeSpecs);

  return (
    <DetailsAccordion title={title}>
      {error || pipeSpecsArray.length === 0 ? (
        <Typography sx={{ fontSize: 10, fontWeight: 400, background: 'white', p: 1 }}>
          Failed to load pipe spec
        </Typography>
      ) : loading ? (
        <SkeletonLoading />
      ) : (
        <>
          {pipeSpecsArray.map(([key, value]) => (
            <Detail key={key} title={key} value={value || 'N/A'} />
          ))}
        </>
      )}
    </DetailsAccordion>
  );
};
